import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import StartIcon from "@mui/icons-material/Start";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useSelector } from "react-redux";
import { GetIsFetching, GetIsLoading } from "../../Redux/Reducers/redux-slice-timetool";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const PlayerControls: React.FC<{
  handleGoTo: (target: string) => void;
  handlePlayOrPause: (nextIsPlayingState: boolean) => void;
  isPlaying: boolean;
}> = ({ handleGoTo, handlePlayOrPause, isPlaying }) => {
  const { t } = useTranslation();
  const isFetching = useSelector(GetIsFetching);
  const isLoading = useSelector(GetIsLoading);
  const theme = useTheme();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        handleGoTo("next_frame");
      } else if (event.key === "ArrowLeft") {
        handleGoTo("previous_frame");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleGoTo]);

  return (
    <Box mr={2} flex={1} display="flex" justifyContent="center">
      <Stack
        sx={{ backgroundColor: theme.palette.background[200], maxWidth: "550px" }}
        display="flex"
        direction="row"
        justifyContent="space-around"
      >
        <Tooltip title={t("tooltip:title.goToStart")}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px", rotate: "180deg" }}
            onClick={() => handleGoTo("start")}
          >
            <StartIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.goToPreviousSegment")}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }}
            onClick={() => handleGoTo("previous_segment")}
          >
            <SkipPreviousIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.goBackwardCountSecond", { count: 1 })}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }}
            onClick={() => handleGoTo("previous_second")}
          >
            <FastRewindIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.goBackwardCountFrame", { count: 1 })}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }}
            onClick={() => handleGoTo("previous_frame")}
          >
            <ArrowLeftIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isPlaying ? t("common:lowerCasePause") : t("common:lowerCasePlay")}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }}
            disabled={isFetching || isLoading}
            onClick={() => handlePlayOrPause(!isPlaying)}
          >
            {isPlaying ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.goForwardCountFrame", { count: 1 })}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }}
            onClick={() => handleGoTo("next_frame")}
          >
            <ArrowRightIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.goForwardCountSecond", { count: 1 })}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }}
            onClick={() => handleGoTo("next_second")}
          >
            <FastForwardIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.goToNextSegment")}>
          <IconButton
            sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }}
            onClick={() => handleGoTo("next_segment")}
          >
            <SkipNextIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("button:goToEnd")}>
          <IconButton sx={{ marginLeft: "6px", marginRight: "6px", padding: "9px" }} onClick={() => handleGoTo("end")}>
            <StartIcon sx={{ rotate: 180 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default PlayerControls;
